import { FFmpeg } from "@ffmpeg/ffmpeg";
import _ from "lodash";

import {
  AiRequestsQuery,
  InputUndress,
  InputUndressType,
} from "@/.gql/graphql";
import { PhotoSettings, Profile, TUndressingFile } from "@/providers";
import { createFile } from "@/utils/converter/createFile";

export const updateUndressingsFiles = (
  collection: AiRequestsQuery["getAiRequests"]["sets"],
  selectedId: string,
  type: keyof PhotoSettings,
  undressingsFiles: TUndressingFile[],
): TUndressingFile[] => {
  const current = collection.find((item) => item.id === selectedId);
  if (!current?.collectionTag) {
    return undressingsFiles;
  }

  const itemsWithCollections = collection.filter(
    (item) => item.collectionTag === current.collectionTag,
  );

  if (itemsWithCollections.some((item) => item.id === selectedId)) {
    return itemsWithCollections.map(({ id }) => ({
      id: undressingsFiles[0].id,
      fileUrl: undressingsFiles[0]?.fileUrl,
      fileExtension: undressingsFiles[0]?.fileExtension,
      [type]: id,
    }));
  }

  return undressingsFiles;
};

export const prepareInputs = (
  undressingsFiles: TUndressingFile[],
  clickSettings: PhotoSettings,
  aiRequestsIds: string[],
  undressType: InputUndressType,
  remix: boolean,
): Promise<InputUndress[]> =>
  Promise.all(
    _(undressingsFiles)
      .map(
        async ({
          id,
          fileExtension,
          mask: canvas,
          set: selectedSet,
          storyline: selectedStoryline,
        }) => {
          try {
            let mask = null;
            if (canvas) {
              const base64Data = canvas.toDataURL();
              mask = await createFile(base64Data, "mask.png");
            }

            const {
              gender,
              set: defaultSet,
              storyline: defaultStoryline,
              prompt,
            } = clickSettings;

            return {
              aiRequestsIds: [
                ...aiRequestsIds,
                selectedSet ?? defaultSet,
                selectedStoryline ?? defaultStoryline,
              ],
              fileKey: `${id}.${fileExtension}`,
              gender,
              mask,
              prompt,
              remix,
              undressType,
            } as InputUndress;
          } catch (err) {
            const error = err as Error;
            console.error(error);
            throw new Error(error.message);
          }
        },
      )
      .compact()
      .value(),
  );

export const fetchRetry = async (
  url: string,
  options: RequestInit = {},
  attempts: number,
): Promise<Response> => {
  let lastError: Error | null = null;

  for (let i = attempts; i >= 0; i--) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`Fetch failed with status ${response.status}`);
      }
      return response;
    } catch (err) {
      lastError = err as Error;
      if (i === 0) {
        throw lastError;
      }
    }
  }

  throw lastError || new Error("Unknown error in fetchRetry");
};

export const checkCoins = (
  profile: NonNullable<Profile>,
  selectedVipSettings: boolean,
  undressingCost: number,
): void => {
  const hasEnoughCoins = selectedVipSettings
    ? profile.coins >= undressingCost
    : profile.coins >= undressingCost || profile.freeCoins >= undressingCost;

  if (!hasEnoughCoins) {
    throw new Error("PATH_NOT_ENOUGH_COINS");
  }
};

export async function cutVideo(
  inputUrl: string,
  startTime: number,
  endTime: number,
): Promise<Blob> {
  if (endTime <= startTime) {
    throw new Error("End time must be greater than start time");
  }

  try {
    const ffmpeg = new FFmpeg();
    await ffmpeg.load();

    const videoFile = await fetch(inputUrl).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.statusText}`);
      }
      return response.arrayBuffer();
    });

    ffmpeg.writeFile("input.mp4", new Uint8Array(videoFile));

    const duration = (endTime - startTime).toString();
    const startHHMMSS = convertToHHMMSS(startTime);

    await ffmpeg.exec([
      "-ss",
      startHHMMSS,
      "-i",
      "input.mp4",
      "-t",
      duration,
      "-c:v",
      "copy",
      "-c:a",
      "copy",
      "output.mp4",
    ]);

    const data = await ffmpeg.readFile("output.mp4");
    return new Blob([data], { type: "video/mp4" });
  } catch (error) {
    console.error(`Failed to cut video from ${inputUrl}:`, error);
    throw new Error("Failed to cut video");
  }
}

function convertToHHMMSS(val: number) {
  const hours = String(Math.floor(val / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((val % 3600) / 60)).padStart(2, "0");
  const seconds = String((val % 60).toFixed(2)).padStart(5, "0");
  return `${hours}:${minutes}:${seconds}`;
}
